import type { DefinitionRow, DefinitionsCatalogBody } from '../../models/catalog.types'
import type { ChangeType, ColumnSubType, ReportColumn } from '../../models/column.types'

const COLUMN_SUB_TYPES: ReadonlySet<string> = new Set([
  'badge',
  'window_cells',
  'currency',
  'numeric',
  'decimal',
  'percentage',
  'meta',
  'date',
  'datetime',
  'image',
  'wsName',
  'status',
  'array',
  'text',
])

const FIELD_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  conversions_value: 'Conversion Value',
  roas: 'ROAS',
}

/** Title-case a snake_case field — the degraded label for catalog misses (08 §12). */
export function prettifyField(field: string): string {
  const override = FIELD_LABEL_OVERRIDES[field]
  if (override) return override
  return field
    .split(/[_\s]+/u)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** Fold the wire vocabulary onto 08's ChangeType: `notr` (neutral) and unknowns become null. */
export function normalizeChangeType(value: unknown): ChangeType {
  return value === 'positive' || value === 'negative' ? value : null
}

function asSubType(value: unknown): ColumnSubType | undefined {
  return typeof value === 'string' && COLUMN_SUB_TYPES.has(value)
    ? (value as ColumnSubType)
    : undefined
}

const HEADER_OVERRIDES_BY_NAME: Readonly<Record<string, string>> = { Benchmark: 'Collected From' }
const HEADER_OVERRIDES_BY_FIELD: Readonly<Record<string, string>> = {
  external_action: 'Conversion Event',
  ad_category: 'Interest Category',
}

function headerFor(row: DefinitionRow): string {
  const catalogName = row.visible_name || prettifyField(row.field)
  return (
    HEADER_OVERRIDES_BY_FIELD[row.field] ?? HEADER_OVERRIDES_BY_NAME[catalogName] ?? catalogName
  )
}

function baseColumn(row: DefinitionRow): ReportColumn {
  const column: ReportColumn = {
    field: row.field,
    header: headerFor(row),
  }
  if (typeof row.visible_name === 'string' && row.visible_name) {
    column.visible_name = row.visible_name
  }
  if (typeof row.description === 'string' && row.description.trim()) {
    column.description = row.description
  }
  const subType = asSubType(row.sub_type)
  if (subType) column.sub_type = subType
  // Forward the catalog glyph key so the metric/dimension logo survives the wire→column seam
  // (contract 08 §5: `headerImage` → PlatformLogo). The payload that carries it is BE-owned
  // (ORP-19313); until it ships this stays undefined and consumers render no icon (no broken img).
  if (typeof row.header_image === 'string' && row.header_image) {
    column.headerImage = row.header_image
  }
  // `is_label` and `invisible` are contract 08 §5 column attributes that v3 received for free from
  // its `{...row}` catalog spread (v3 `columnStore.ts:205`). This allowlisted mapper dropped them,
  // leaving both flags with consumers but no producer — the filter picker's Labels group and every
  // `!column.invisible` guard were dead. Forward them so the seam stops narrowing the field set
  // (ORP-19849). Only set when true: absent stays absent, so exact-shape assertions still hold.
  // Same class of gap as `is_label` above: v3's `{...row}` spread carried the catalog's `url`
  // attribute, which names the row field a cell links to. This allowlist dropped it, so no
  // table could turn a cell into a link.
  if (typeof row.url === 'string' && row.url) column.url = row.url
  if (row.is_label === true) column.is_label = true
  if (row.invisible === true) column.invisible = true
  // GA4 total override (ORP-25673): flagged columns take their values from `data_ga4`/`total_ga4`.
  if (row['ga4_total_override'] === true) column.ga4_total_override = true
  return column
}

export interface NormalizedDefinitions {
  metrics: Record<string, ReportColumn>
  dimensions: Record<string, ReportColumn>
}

/** Pure wire→ReportColumn mapper for `/catalog/definitions/` bodies (v3 columnStore parity:
 *  dimensions detect image columns, metrics default to numeric). Rows without a string
 *  `field` are dropped rather than crashing the catalog. */
export function normalizeDefinitions(body: DefinitionsCatalogBody): NormalizedDefinitions {
  const dimensions: Record<string, ReportColumn> = {}
  for (const row of body.dimensions?.data ?? []) {
    if (typeof row?.field !== 'string' || !row.field) continue
    const column = baseColumn(row)
    if (column.sub_type === 'image' || Boolean(row['link'] ?? row['link1'] ?? row['link2'])) {
      column.isImage = true
    }
    dimensions[row.field] = column
  }

  const metrics: Record<string, ReportColumn> = {}
  for (const row of body.metrics?.data ?? []) {
    if (typeof row?.field !== 'string' || !row.field) continue
    const column = baseColumn(row)
    column.sub_type ??= 'numeric'
    column.numeric = true
    // `notr` is an explicit "neutral" (null). A metric that publishes no polarity stays
    // undefined so the delta cell can apply v3's default of higher-is-better.
    const changeType = normalizeChangeType(row.change_type)
    if (changeType !== null || row.change_type === 'notr') column.change_type = changeType
    metrics[row.field] = column
  }

  return { metrics, dimensions }
}
